import { getRuleByPolicyDiagram } from "./convertPolicyStructure";
import { isWithinWorkingHours } from "./WorkingHours";
import { matchIPv4 } from "./IPFilter";

export type ResourceType = 'WEB' | 'NATIVE' | 'API' | 'DATA';
export type Decision = 'Permit' | 'Deny' | 'PolicyDecisionError';
export type CombiningAlgorithm = 'deny-overrides' | 'permit-overrides' | 'first-applicable';
type ReasonType =
    | 'ip_address'
    | 'date_time'
    | 'subject'
    | 'action'
    | 'resource'
    | 'environment'
    | 'condition'
    | 'policy'
    | 'system';

interface Resource {
    name: string;
    type: ResourceType;
    param?: any;
}

interface Environment {
    name?: string;
    type?: ResourceType;
    timestamp?: string;
}

interface PolicyRequest {
    id: string;
    subject?: any;
    resource: Resource;
    action: string;
    environment: {
        resource?: Environment;
        public_ip?: string;
    };
}

export interface DecisionReason {
    type: ReasonType;
    message: string;
}

export interface RuleResult {
    ruleId: string;
    decision: Decision;
    effect?: Decision;
    targetMatched: boolean;
    actionMatched: boolean;
    reasons: DecisionReason[];
}

export interface SimulationResult {
    id: string;
    decision: Decision;
    type?: ResourceType;
    combiningAlgorithm: CombiningAlgorithm;
    ruleResults: RuleResult[];
    reasons: DecisionReason[];
    message?: string;
}

interface TargetEvaluation {
    matches: boolean;
    reasons: DecisionReason[];
}

interface EnvironmentEvaluation {
    decision: 'Match' | 'Deny' | 'PolicyDecisionError';
    reasons: DecisionReason[];
}

interface SubjectEvaluation {
    decision: 'Match' | 'Deny' | 'PolicyDecisionError';
    reasons: DecisionReason[];
}

class PolicySimulator {
    policyDiagram: any;
    policies: any[];
    combiningAlgorithm: CombiningAlgorithm;

    constructor(
        policyDiagram: any,
        combiningAlgorithm: CombiningAlgorithm = 'permit-overrides'
    ) {
        this.policyDiagram = policyDiagram;
        this.policies = [];
        this.combiningAlgorithm = combiningAlgorithm;

        getRuleByPolicyDiagram(this.policyDiagram)
            .then((rules: any[]) => {
                this.policies = rules;
                // console.log("rules:", rules);
            })
            .catch(() => {
                this.policies = [];
            });
    }

    createPolicyRequest(
        id: string,
        resource: Resource,
        action: string,
        environment?: Environment,
        public_ip?: string
    ): PolicyRequest {
        return {
            id,
            resource,
            action,
            environment: {
                ...(environment ? { resource: environment } : {}),
                ...(public_ip ? { public_ip } : {})
            }
        };
    }

    /**
     * Target ใช้เลือก candidate rules เท่านั้น:
     * resource type/name/path
     */
    private evaluateTarget(policy: any, request: PolicyRequest): TargetEvaluation {
        const reasons: DecisionReason[] = [];

        if (
            policy.resource?.type !== request.resource.type ||
            policy.resource?.name !== request.resource.name
        ) {
            reasons.push({
                type: 'resource',
                message: "The requested resource type or name does not match the rule target."
            });
            return { matches: false, reasons };
        }

        if (request.resource.type === 'API') {
            const policyPath = policy.resource?.param?.path;
            const requestPath = request.resource?.param?.path;
            let pathMatches = false;

            if (typeof policyPath === 'string' && typeof requestPath === 'string') {
                pathMatches = policyPath.endsWith('*')
                    ? requestPath.startsWith(policyPath.slice(0, -1))
                    : requestPath === policyPath;
            }

            if (!pathMatches) {
                reasons.push({
                    type: 'resource',
                    message: `Request path '${requestPath}' does not match rule path '${policyPath}'.`
                });
            }
        }

        return { matches: reasons.length === 0, reasons };
    }

    /**
     * Policy ไม่กำหนด resources หมายถึงไม่จำกัด resource ต้นทาง
     * แต่ถ้ากำหนด request ต้องส่ง environment.resource ที่ตรงกัน
     */
    private evaluateEnvironmentResource(
        policy: any,
        request: PolicyRequest
    ): EnvironmentEvaluation {
        const allowedResources = policy.environment?.resources ?? [];

        if (!Array.isArray(allowedResources)) {
            return {
                decision: 'PolicyDecisionError',
                reasons: [{
                    type: 'environment',
                    message: "The rule's environment resources must be an array."
                }]
            };
        }

        if (allowedResources.length === 0) {
            return { decision: 'Match', reasons: [] };
        }

        const hasInvalidResource = allowedResources.some((resource: Environment) =>
            !resource ||
            typeof resource.type !== 'string' ||
            typeof resource.name !== 'string' ||
            resource.type.trim() === '' ||
            resource.name.trim() === ''
        );

        if (hasInvalidResource) {
            return {
                decision: 'PolicyDecisionError',
                reasons: [{
                    type: 'environment',
                    message: "The rule contains an invalid environment resource."
                }]
            };
        }

        const requestResource = request.environment?.resource;
        const matches = allowedResources.some((resource: Environment) =>
            resource.type?.toUpperCase() === requestResource?.type?.toUpperCase() &&
            resource.name === requestResource?.name
        );

        if (matches) {
            return { decision: 'Match', reasons: [] };
        }

        return {
            decision: 'Deny',
            reasons: [{
                type: 'environment',
                message: requestResource?.name
                    ? `Requests from '${requestResource.type}:${requestResource.name}' are not allowed by this rule.`
                    : "The rule requires an environment resource, but the request did not provide one."
            }]
        };
    }

    private evaluateExpression(expression: unknown, request: PolicyRequest): boolean {
        if (expression === null || expression === undefined || expression === "") {
            return true;
        }
        if (typeof expression !== 'string') {
            throw new Error("The policy expression must be a string.");
        }

        // Expressions produced by convertPolicyStructure refer to `request`.
        // This remains isolated here so it can be replaced by an AST evaluator later.
        return Boolean(eval(expression));
    }

    /**
     * ป้องกันไม่ให้ subject constraint นำค่า undefined ไปเปรียบเทียบใน eval
     * โดย throw ทันทีเมื่อ expression เข้าถึง attribute ที่ไม่มีหรือเป็น undefined
     */
    private createStrictSubjectProxy(value: any, path = 'subject'): any {
        if (value === null || typeof value !== 'object') {
            return value;
        }

        const prototype = Object.getPrototypeOf(value);
        const canProxy = Array.isArray(value) || prototype === Object.prototype || prototype === null;
        if (!canProxy) {
            return value;
        }

        return new Proxy(value, {
            get: (target, property, receiver) => {
                const propertyPath = `${path}.${String(property)}`;
                if (!Reflect.has(target, property)) {
                    throw new Error(`Required ${propertyPath} is missing.`);
                }

                const propertyValue = Reflect.get(target, property, receiver);
                if (propertyValue === undefined) {
                    throw new Error(`Required ${propertyPath} is undefined.`);
                }

                return this.createStrictSubjectProxy(propertyValue, propertyPath);
            }
        });
    }

    private evaluateSubjectConstraint(policy: any, request: PolicyRequest): SubjectEvaluation {
        const constraint = policy.subject_constraint;
        if (constraint === null || constraint === undefined || constraint === '') {
            return { decision: 'Match', reasons: [] };
        }

        if (typeof constraint !== 'string') {
            return {
                decision: 'PolicyDecisionError',
                reasons: [{
                    type: 'subject',
                    message: "The rule's subject constraint must be a string."
                }]
            };
        }

        const subject = request.subject;
        if (
            subject === null ||
            typeof subject !== 'object' ||
            Array.isArray(subject) ||
            Object.keys(subject).length === 0
        ) {
            return {
                decision: 'PolicyDecisionError',
                reasons: [{
                    type: 'subject',
                    message: "The rule requires subject attributes, but the request subject is missing or empty."
                }]
            };
        }

        try {
            const strictRequest: PolicyRequest = {
                ...request,
                subject: this.createStrictSubjectProxy(subject)
            };
            const matches = this.evaluateExpression(constraint, strictRequest);
            return matches
                ? { decision: 'Match', reasons: [] }
                : {
                    decision: 'Deny',
                    reasons: [{
                        type: 'subject',
                        message: "The subject does not satisfy the rule constraint."
                    }]
                };
        } catch (error: any) {
            return {
                decision: 'PolicyDecisionError',
                reasons: [{
                    type: 'subject',
                    message: error?.message ?? String(error)
                }]
            };
        }
    }

    /**
     * ประเมิน candidate rule จาก action, subject, environment และ condition
     */
    private evaluateRule(policy: any, request: PolicyRequest): RuleResult {
        const ruleId = policy.rule_id ?? "";
        const reasons: DecisionReason[] = [];
        let hasPolicyDecisionError = false;
        let actionMatched = false;

        try {
            const hasInvalidActions =
                !Array.isArray(policy.action) ||
                policy.action.some((action: unknown) =>
                    typeof action !== 'string' || action.trim() === ''
                );

            if (hasInvalidActions) {
                hasPolicyDecisionError = true;
                reasons.push({
                    type: 'action',
                    message: "The rule contains invalid allowed actions."
                });
            } else {
                const allowedActions = policy.action.map((action: string) =>
                    action.trim().toUpperCase()
                );
                const requestAction = typeof request.action === 'string'
                    ? request.action.trim().toUpperCase()
                    : "";
                actionMatched =
                    requestAction !== "" &&
                    allowedActions.includes(requestAction);

                if (!actionMatched) {
                    reasons.push({
                        type: 'action',
                        message: `Action '${request.action}' is not allowed by this rule.`
                    });
                }
            }

            const subjectResult = this.evaluateSubjectConstraint(policy, request);
            if (subjectResult.decision !== 'Match') {
                reasons.push(...subjectResult.reasons);
                hasPolicyDecisionError =
                    hasPolicyDecisionError ||
                    subjectResult.decision === 'PolicyDecisionError';
            }

            const environmentResult = this.evaluateEnvironmentResource(policy, request);
            if (environmentResult.decision !== 'Match') {
                reasons.push(...environmentResult.reasons);
                hasPolicyDecisionError =
                    hasPolicyDecisionError ||
                    environmentResult.decision === 'PolicyDecisionError';
            }

            const policyTime = policy.environment?.time;
            if (policyTime && Object.keys(policyTime).length > 0) {
                const timestamp = request.environment?.resource?.timestamp;
                let requestTime: Date | undefined;
                let hasValidTimestamp = true;

                if (timestamp) {
                    requestTime = new Date(timestamp);
                    if (Number.isNaN(requestTime.getTime())) {
                        hasValidTimestamp = false;
                        hasPolicyDecisionError = true;
                        reasons.push({
                            type: 'date_time',
                            message: `Request timestamp '${timestamp}' is invalid.`
                        });
                    }
                }

                if (
                    hasValidTimestamp &&
                    !isWithinWorkingHours(policyTime, requestTime)
                ) {
                    reasons.push({
                        type: 'date_time',
                        message: "The request is outside the rule's allowed working hours."
                    });
                }
            }

            const allowedIpAddresses = policy.environment?.ip_address;
            if (allowedIpAddresses) {
                const publicIp = request.environment?.public_ip;
                if (
                    !publicIp ||
                    !matchIPv4(publicIp, allowedIpAddresses.split(','))
                ) {
                    reasons.push({
                        type: 'ip_address',
                        message: publicIp
                            ? `Public IP '${publicIp}' is not authorized by this rule.`
                            : "The rule requires a public IP, but the request did not provide one."
                    });
                }
            }

            if (!this.evaluateExpression(policy.condition, request)) {
                reasons.push({
                    type: 'condition',
                    message: "The request does not satisfy the rule condition."
                });
            }

            if (hasPolicyDecisionError) {
                return {
                    ruleId,
                    decision: 'PolicyDecisionError',
                    targetMatched: true,
                    actionMatched,
                    reasons
                };
            }

            if (reasons.length > 0) {
                return { ruleId, decision: 'Deny', targetMatched: true, actionMatched, reasons };
            }

            const normalizedEffect = String(policy.effect).toLowerCase();
            if (normalizedEffect === 'permit') {
                return {
                    ruleId,
                    decision: 'Permit',
                    effect: 'Permit',
                    targetMatched: true,
                    actionMatched,
                    reasons: []
                };
            }
            if (normalizedEffect === 'deny' || normalizedEffect === 'denied') {
                return {
                    ruleId,
                    decision: 'Deny',
                    effect: 'Deny',
                    targetMatched: true,
                    actionMatched,
                    reasons: [{
                        type: 'policy',
                        message: "The applicable rule has a Deny effect."
                    }]
                };
            }

            return {
                ruleId,
                decision: 'PolicyDecisionError',
                targetMatched: true,
                actionMatched,
                reasons: [{
                    type: 'policy',
                    message: `Rule effect '${policy.effect}' is invalid.`
                }]
            };
        } catch (error: any) {
            return {
                ruleId,
                decision: 'PolicyDecisionError',
                targetMatched: true,
                actionMatched,
                reasons: [{
                    type: 'system',
                    message: error?.message ?? String(error)
                }]
            };
        }
    }

    private combine(
        ruleResults: RuleResult[],
        algorithm: CombiningAlgorithm
    ): Decision {
        if (algorithm === 'first-applicable') {
            return ruleResults[0]?.decision ?? 'Deny';
        }

        const precedence: Decision[] = algorithm === 'permit-overrides'
            ? ['Permit', 'PolicyDecisionError', 'Deny']
            : ['Deny', 'PolicyDecisionError', 'Permit'];

        return precedence.find(decision =>
            ruleResults.some(result => result.decision === decision)
        ) ?? 'Deny';
    }

    private getFinalReasons(
        decision: Decision,
        ruleResults: RuleResult[]
    ): DecisionReason[] {
        let contributingResults = ruleResults.filter(result =>
            result.decision === decision
        );

        if (decision === 'Deny') {
            const actionMatchedResults = contributingResults.filter(result =>
                result.targetMatched && result.actionMatched
            );
            if (actionMatchedResults.length > 0) {
                contributingResults = actionMatchedResults;
            }
        }
        const sourceResults = contributingResults.length > 0
            ? contributingResults
            : ruleResults;

        const uniqueReasons = new Map<string, DecisionReason>();
        sourceResults.flatMap(result => result.reasons).forEach(reason => {
            uniqueReasons.set(`${reason.type}:${reason.message}`, reason);
        });

        return [...uniqueReasons.values()];
    }

    async simulate(
        policyRequest: PolicyRequest,
        profile: any,
        combiningAlgorithm: CombiningAlgorithm = this.combiningAlgorithm
    ): Promise<SimulationResult> {
        const request: PolicyRequest = { ...policyRequest, subject: profile };
        const answerRules = this.policies.filter(policy =>
            String(policy.rule_id ?? '') === String(request.id ?? '')
        );

        if (answerRules.length !== 1) {
            const reason: DecisionReason = {
                type: 'policy',
                message: answerRules.length === 0
                    ? `No policy rule matches request id '${request.id}'.`
                    : `Multiple policy rules match request id '${request.id}'.`
            };

            return {
                id: request.id,
                decision: 'PolicyDecisionError',
                type: request.resource?.type,
                combiningAlgorithm,
                ruleResults: [],
                reasons: [reason],
                message: reason.message
            };
        }

        // Simulator ใช้ request.id ชี้ไปยัง rule เฉลยเพียงตัวเดียว
        const answerRule = answerRules[0];
        const target = this.evaluateTarget(answerRule, request);
        const ruleResults: RuleResult[] = [
            target.matches
                ? this.evaluateRule(answerRule, request)
                : {
                    ruleId: answerRule.rule_id ?? "",
                    decision: 'Deny',
                    targetMatched: false,
                    actionMatched: false,
                    reasons: target.reasons
                }
        ];

        const decision = this.combine(ruleResults, combiningAlgorithm);
        const hasApiTargetMismatch =
            request.resource.type === 'API' &&
            !target.matches;
        const reasons: DecisionReason[] = hasApiTargetMismatch
            ? [{
                type: 'resource',
                message: `No API resource matches type '${request.resource.type}', name '${request.resource.name}', and path '${request.resource.param?.path}'.`
            }]
            : this.getFinalReasons(decision, ruleResults);

        return {
            id: request.id,
            decision,
            type: request.resource?.type,
            combiningAlgorithm,
            ruleResults,
            reasons,
            ...(reasons.length > 0
                ? { message: reasons.map(reason => reason.message).join(" ") }
                : {})
        };
    }
}

export default PolicySimulator;
